import { useEffect, useState } from "react";
import API, { apiErrorMessage } from "../lib/api";
import { useOwner } from "./OwnerContext";

const statuses = ["new", "contacted", "quoted", "archived"];

export default function Inbox() {
  const { phase } = useOwner();
  const [list, setList] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const { logout } = useOwner();

  const load = async () => {
    setError("");
    try {
      const { data } = await API.get("/owner/enquiries");
      setList(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Your session has expired. Please sign in again.");
        logout();
      } else {
        setError(apiErrorMessage(e, "Could not load enquiries."));
      }
    }
  };

  useEffect(() => {
    if (phase === "authed") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const updateStatus = async (id, status) => {
    try {
      await API.patch(`/owner/enquiries/${id}`, { status });
      setList((l) => l.map((item) => (item.id === id ? { ...item, status } : item)));
    } catch (e) {
      setError(apiErrorMessage(e, "Could not update the enquiry."));
    }
  };

  if (error && !list) {
    return <p role="alert" className="rounded-xl border border-accent bg-lavender p-5 text-sm text-plum">{error}</p>;
  }
  if (!list) {
    return <p className="font-mono text-sm text-muted">Loading enquiries…</p>;
  }
  const q = query.trim().toLowerCase();
  const filtered = list.filter((item) => {
    if (statusFilter !== "all" && item.status !== statusFilter) return false;
    if (!q) return true;
    return [item.name, item.email, item.company, item.service, item.message, item.budget, item.start_date]
      .some((v) => (v || "").toLowerCase().includes(q));
  });
  const counts = statuses.reduce((acc, st) => ({ ...acc, [st]: list.filter((i) => i.status === st).length }), {});

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted" data-testid="inbox-count">
          {filtered.length === list.length
            ? `${list.length} ${list.length === 1 ? "enquiry" : "enquiries"} · newest first`
            : `${filtered.length} of ${list.length} enquiries shown`}
        </p>
        <button
          data-testid="inbox-refresh-button"
          onClick={load}
          className="rounded-full border border-line px-4 py-2 text-xs font-semibold text-ink hover:border-plum hover:text-plum"
        >
          Refresh
        </button>
      </div>
      {list.length > 0 && (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center" data-testid="inbox-filters">
          <label className="relative flex-1">
            <span className="sr-only">Search enquiries</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, company, service or message…"
              data-testid="inbox-search-input"
              className="w-full rounded-full border border-line bg-white px-5 py-2.5 text-sm text-ink placeholder:text-muted/60 focus:border-accent focus:outline-none"
            />
          </label>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by status">
            {["all", ...statuses].map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setStatusFilter(st)}
                aria-pressed={statusFilter === st}
                data-testid={`inbox-status-filter-${st}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold capitalize transition-colors ${
                  statusFilter === st ? "bg-plum text-white" : "border border-line bg-white text-ink hover:border-plum hover:text-plum"
                }`}
              >
                {st}{st !== "all" && <span className="ml-1 opacity-70">{counts[st]}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <p role="alert" className="mt-4 rounded-xl border border-accent bg-lavender p-4 text-sm text-plum">{error}</p>}
      {list.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-line bg-white p-10 text-center" data-testid="inbox-empty-state">
          <p className="font-serif text-lg text-ink">No enquiries yet.</p>
          <p className="mt-2 text-sm text-muted">
            When someone submits the contact form, their enquiry will appear here.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-line bg-white p-10 text-center" data-testid="inbox-no-results">
          <p className="font-serif text-lg text-ink">No enquiries match.</p>
          <button type="button" onClick={() => { setQuery(""); setStatusFilter("all"); }} data-testid="inbox-clear-filters"
            className="mt-4 text-sm font-semibold text-plum underline underline-offset-4 hover:text-accent">
            Clear search and filters
          </button>
        </div>
      ) : (
        <ul className="mt-8 space-y-5">
          {filtered.map((item) => (
            <li key={item.id} data-testid="inbox-item" className="rounded-2xl border border-line bg-white p-6 sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-serif text-lg font-medium text-ink">{item.name}</p>
                  <p className="mt-1 text-sm text-muted">
                    <a href={`mailto:${item.email}`} className="text-plum hover:text-accent" data-testid="inbox-email-link">
                      {item.email}
                    </a>
                    {item.company && <span> · {item.company}</span>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-[11px] text-muted">
                    {new Date(item.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                  {item.alert && (
                    <p data-testid="enquiry-alert-status" className={`mt-1 font-mono text-[10px] uppercase tracking-[0.12em] ${item.alert.state === "sent" ? "text-teal-700" : "text-plum"}`}
                      title={item.alert.error || item.alert.to}>
                      {item.alert.state === "sent" ? "Email alert sent" : "Email alert failed"}
                    </p>
                  )}
                  {item.confirmation && (
                    <p data-testid="enquiry-confirmation-status" className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-muted" title={item.confirmation.error || item.confirmation.reason || ""}>
                      Visitor confirmation {item.confirmation.state}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                {item.service && <span className="rounded-full bg-lavender px-3 py-1 font-medium text-plum">{item.service}</span>}
                {item.budget && <span className="rounded-full border border-line px-3 py-1 text-muted">Budget: {item.budget}</span>}
                {item.start_date && <span className="rounded-full border border-line px-3 py-1 text-muted">Start: {item.start_date}</span>}
              </div>
              <p className="mt-4 whitespace-pre-line border-t border-line pt-4 text-sm leading-relaxed text-ink">
                {item.message}
              </p>
              <div className="mt-5 flex items-center gap-3">
                <a
                  href={`mailto:${item.email}?subject=${encodeURIComponent(`Re: your enquiry${item.service ? ` — ${item.service}` : ""} — Savvy Stephanie`)}&body=${encodeURIComponent(`Hi ${item.name.split(" ")[0]},\n\nThank you for your enquiry. `)}`}
                  data-testid="enquiry-reply-button"
                  className="inline-flex items-center gap-1.5 rounded-full bg-plum px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-ink"
                >
                  Reply by email <span aria-hidden="true">↗</span>
                </a>
                <label htmlFor={`status-${item.id}`} className="text-xs font-medium text-muted">
                  Status
                </label>
                <select
                  id={`status-${item.id}`}
                  data-testid="enquiry-status-select"
                  value={item.status}
                  onChange={(e) => updateStatus(item.id, e.target.value)}
                  className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-plum"
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
